// frontend/src/app/project-corpus/domain/import-job-dto.ts
import { ImportJob, ImportJobStatus, ImportSourceType } from './types';

/** Backend DTO shape returned by GET /api/v1/import-jobs and the import-job POST endpoints. */
export interface ImportJobDto {
  id: number;
  status: string; // RUNNING | COMPLETED | FAILED
  source: string;
  startedByUserId: number;
  actorDisplayName: string;
  createdAt: string; // ISO-8601
  finishedAt: string | null;
  workTypes?: string[];
  sourceTypes?: string[];
  papersTotal: number;
  papersComplete: number;
  papersSkipped: number;
  papersMalformed: number;
  itemsAdded: number;
  itemsNeedingAttention: number;
  title: string | null;
  failureReason: string | null;
  papersIncompleteRemoved: number;
  papersUndone: number;
}

function describeSource(source: ImportSourceType): string {
  switch (source) {
    case 'OPENALEX':
      return 'Search import';
    case 'PDF':
      return 'PDF upload';
    case 'BIBTEX':
      return 'BibTeX import';
    case 'GROUP_CORPUS':
      return 'Group corpus import';
    default:
      return 'Search import';
  }
}

/**
 * Backend stores the OpenAlex query as `"query:{query}"` in the source field. Parse it back to a
 * source type plus descriptor. Other sources are stored as-is.
 */
export function parseBackendSource(raw: string): {
  source: ImportSourceType;
  descriptor: string;
} {
  if (raw.startsWith('query:')) {
    return { source: 'OPENALEX', descriptor: raw.slice(6) };
  }
  const known: Record<string, ImportSourceType> = {
    PDF: 'PDF',
    BIBTEX: 'BIBTEX',
    GROUP_CORPUS: 'GROUP_CORPUS',
  };
  const src = known[raw];
  if (src) {
    return { source: src, descriptor: describeSource(src) };
  }
  return { source: 'OPENALEX', descriptor: raw };
}

export function mapImportJobStatus(backend: string): ImportJobStatus {
  switch (backend) {
    case 'RUNNING':
      return 'RUNNING';
    case 'COMPLETED':
      return 'COMPLETED';
    case 'FAILED':
      return 'FAILED';
    case 'UNDONE':
      return 'UNDONE';
    default:
      return 'COMPLETED';
  }
}

/** Maps a single backend DTO to the frontend ImportJob model. */
export function mapImportJobDto(dto: ImportJobDto, projectId: string): ImportJob {
  const parsed = parseBackendSource(dto.source);
  return {
    id: String(dto.id),
    projectId,
    source: parsed.source,
    rawSource: dto.source,
    status: mapImportJobStatus(dto.status),
    startedAt: dto.createdAt,
    finishedAt: dto.finishedAt ?? null,
    actorDisplayName: dto.actorDisplayName,
    itemsAdded: dto.itemsAdded,
    itemsNeedingAttention: dto.itemsNeedingAttention,
    descriptor: parsed.descriptor,
    papersTotal: dto.papersTotal ?? 0,
    papersComplete: dto.papersComplete ?? 0,
    papersSkipped: dto.papersSkipped ?? 0,
    papersMalformed: dto.papersMalformed ?? 0,
    workTypes: dto.workTypes ?? [],
    sourceTypes: dto.sourceTypes ?? [],
    title: dto.title ?? null,
    failureReason: dto.failureReason ?? null,
    startedByUserId: String(dto.startedByUserId),
    papersIncompleteRemoved: dto.papersIncompleteRemoved ?? 0,
    papersUndone: dto.papersUndone ?? 0,
  };
}
